import { Host, h } from "@stencil/core";
import { getControlRegisterProperty } from "../../../../common/registry-properties";
import { DISABLED_CLASS, NAVIGATION_LIST_ITEM_EXPORT_PARTS, NAVIGATION_LIST_ITEM_PARTS_DICTIONARY } from "../../../../common/reserved-names";
import { tokenMap, tokenMapExportParts, updateDirectionInImageCustomVar } from "../../../../common/utils";
import { NAVIGATION_LIST_INITIAL_LEVEL } from "../../utils";
import { getNavigationListItemLevelPart } from "./utils";
let GET_IMAGE_PATH_CALLBACK_REGISTRY;
/**
 * @status experimental
 */
export class ChNavigationListItem {
    constructor() {
        this.caption = undefined;
        this.disabled = false;
        this.expandable = true;
        this.expandableButton = "decorative";
        this.expandableButtonPosition = "start";
        this.expanded = undefined;
        this.getImagePathCallback = undefined;
        this.level = undefined;
        this.link = undefined;
        this.model = undefined;
        this.navigationListExpanded = true;
        this.selected = false;
        this.selectedLinkIndicator = false;
        this.showCaptionOnCollapse = "inline";
        this.startImgSrc = undefined;
        this.startImgType = "background";
        this.tooltipDelay = 100;
    }
    #startImage;
    // Refs
    #actionRef;
    el;
    getImagePathCallbackChanged() {
        this.#startImage = this.#computeImage();
    }
    #computeImage = () => {
        if (!this.startImgSrc) {
            return undefined;
        }
        const getImagePathCallback = this.getImagePathCallback ?? GET_IMAGE_PATH_CALLBACK_REGISTRY;
        if (!getImagePathCallback) {
            return undefined;
        }
        const img = getImagePathCallback(this.model);
        return img
            ? updateDirectionInImageCustomVar(img, "start")
            : undefined;
    };
    #renderCaption = (navigationListCollapsed, levelPart) => {
        return navigationListCollapsed &&
            this.showCaptionOnCollapse === "tooltip" ? (h("ch-tooltip", { key: "tooltip",
            // We can't use this.el because in not a focusable element. Non
            // focusable elements generate issue with the "mouseleave" and
            // "focusout" events
            actionElement: this.#actionRef ?? null, actionElementAccessibleName: this.caption, blockAlign: "center", inlineAlign: "outside-end", delay: this.tooltipDelay, exportparts: tokenMapExportParts({
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.CAPTION]: true,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.NAVIGATION_LIST_COLLAPSED]: true,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.TOOLTIP]: true,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.SELECTED]: this.selected,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: !this.selected,
                [levelPart]: true
            }, "window") }, this.caption)) : (h("span", { key: "caption", class: "caption", part: tokenMap({
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.CAPTION]: true,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.NAVIGATION_LIST_COLLAPSED]: navigationListCollapsed,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.SELECTED]: this.selected,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: !this.selected,
                [levelPart]: true
            }) }, this.caption));
    };
    #renderContent = (evenLevelParts, levelPart, hasExpandableButton, expandableButtonPosition) => {
        const navigationListCollapsed = !this.navigationListExpanded;
        // Classes
        const startImageClasses = this.#startImage?.classes;
        const classes = {
            action: true,
            "action--navigation-list-collapsed": navigationListCollapsed,
            [DISABLED_CLASS]: this.disabled,
            [`start-img-type--${this.startImgType ?? "background"} pseudo-img--start`]: !!this.#startImage,
            [startImageClasses]: !!startImageClasses,
            "expandable-button": hasExpandableButton,
            [`expandable-button--expanded-${this.expandableButtonPosition}`]: hasExpandableButton && this.expanded,
            [`expandable-button--collapsed-${this.expandableButtonPosition}`]: hasExpandableButton && !this.expanded,
            "expandable-button--end": hasExpandableButton && expandableButtonPosition === "end"
        };
        return this.link ? (h("a", { key: "hyperlink", role: this.disabled ? "link" : undefined, "aria-current": this.selected ? "page" : undefined, "aria-disabled": this.disabled ? "true" : undefined, class: classes, style: this.#startImage?.styles ?? undefined, part: tokenMap({
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.ACTION]: true,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.LINK]: true,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.SELECTED]: this.selected,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: !this.selected,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.EXPAND_BUTTON]: hasExpandableButton,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.START]: hasExpandableButton && expandableButtonPosition === "start",
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.END]: hasExpandableButton && expandableButtonPosition === "end",
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.NAVIGATION_LIST_COLLAPSED]: navigationListCollapsed,
                [evenLevelParts]: true,
                [levelPart]: true
            }), href: !this.disabled ? this.link.url : undefined,
            // TODO: Add unit tests for this
            rel: !this.disabled ? this.link.rel : undefined,
            // TODO: Add unit tests for this
            target: !this.disabled ? this.link.target : undefined, ref: el => (this.#actionRef = el) }, this.#renderCaption(navigationListCollapsed, levelPart))) : (h("button", { key: "button", class: classes, style: this.#startImage?.styles ?? undefined, part: tokenMap({
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.ACTION]: true,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.BUTTON]: true,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.EXPAND_BUTTON]: hasExpandableButton,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.START]: hasExpandableButton && expandableButtonPosition === "start",
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.END]: hasExpandableButton && expandableButtonPosition === "end",
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.NAVIGATION_LIST_COLLAPSED]: navigationListCollapsed,
                [evenLevelParts]: true,
                [levelPart]: true
            }), disabled: this.disabled, type: "button", ref: el => (this.#actionRef = el) }, this.#renderCaption(navigationListCollapsed, levelPart)));
    };
    connectedCallback() {
        // Initialize default getImagePathCallback
        GET_IMAGE_PATH_CALLBACK_REGISTRY ??= getControlRegisterProperty("getImagePathCallback", "ch-navigation-list-render");
        this.#startImage = this.#computeImage();
        // Static attributes that we including in the Host functional component to
        // eliminate additional overhead
        this.el.setAttribute("role", "listitem");
        this.el.setAttribute("exportparts", NAVIGATION_LIST_ITEM_EXPORT_PARTS);
        this.el.style.setProperty("--level", `${this.level}`);
    }
    render() {
        const levelPart = `level-${this.level}`;
        const evenLevel = this.level % 2 === 0;
        const evenLevelParts = getNavigationListItemLevelPart(evenLevel);
        const hasExpandableButton = this.expandable &&
            this.navigationListExpanded &&
            this.expandableButton === "decorative";
        const expandableButtonPosition = this.expandableButtonPosition;
        return (h(Host, { key: '21b4cb4c1716821a7a1fced7cfa2a16d5dbc89f6', class: {
                expandable: this.expandable,
                "expandable--expanded": this.expanded,
                selected: this.selected && this.selectedLinkIndicator
            } }, this.#renderContent(evenLevelParts, levelPart, hasExpandableButton, expandableButtonPosition), this.selected && this.selectedLinkIndicator && (h("div", { key: '18cc16cd7aa37978c85f2980084dc75f30d83bbb', class: "indicator", part: tokenMap({
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.INDICATOR]: true,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled
            }) })), this.expandable && (h("div", { key: '4ba934b5f8bdf328a47533189cea2127829e564f', role: "list", class: {
                expandable: true,
                "expandable--collapsed": !this.expanded
            }, part: tokenMap({
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.GROUP]: true,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.SELECTED]: this.selected,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: !this.selected,
                [evenLevelParts]: this.level !== NAVIGATION_LIST_INITIAL_LEVEL,
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.START]: hasExpandableButton && expandableButtonPosition === "start",
                [NAVIGATION_LIST_ITEM_PARTS_DICTIONARY.END]: hasExpandableButton && expandableButtonPosition === "end",
                [levelPart]: true
            }) }, h("slot", { key: '294a84376b65a3c2c5db0ca617cfe8690684c7c2' })))));
    }
    static get is() { return "ch-navigation-list-item"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["navigation-list-item.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["navigation-list-item.css"]
        };
    }
    static get properties() {
        return {
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the caption of the control"
                },
                "attribute": "caption",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "expandable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the control contains sub items."
                },
                "attribute": "expandable",
                "reflect": false,
                "defaultValue": "true"
            },
            "expandableButton": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"decorative\" | \"no\"",
                    "resolved": "\"decorative\" | \"no\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies what kind of expandable button is displayed in the items by\ndefault.\n - `\"decorative\"`: Only a decorative icon is rendered to display the state\n    of the item."
                },
                "attribute": "expandable-button",
                "reflect": false,
                "defaultValue": "\"decorative\""
            },
            "expandableButtonPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"start\" | \"end\"",
                    "resolved": "\"end\" | \"start\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the position of the expandable button in reference of the action\nelement of the items\n - `\"start\"`: Expandable button is placed before the action element.\n - `\"end\"`: Expandable button is placed after the action element."
                },
                "attribute": "expandable-button-position",
                "reflect": false,
                "defaultValue": "\"start\""
            },
            "expanded": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the control is expanded or collapsed."
                },
                "attribute": "expanded",
                "reflect": false
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    imageSrc: NavigationListItemModel\n  ) => GxImageMultiState | undefined",
                    "resolved": "(imageSrc: NavigationListItemModel) => GxImageMultiState",
                    "references": {
                        "NavigationListItemModel": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/components/navigation-list/types.ts::NavigationListItemModel"
                        },
                        "GxImageMultiState": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::GxImageMultiState"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nstartImgSrc needs to be resolved."
                }
            },
            "level": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies at which level of the navigation list is rendered the control."
                },
                "attribute": "level",
                "reflect": false
            },
            "link": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ItemLink | undefined",
                    "resolved": "{ url: string; rel?: string; target?: \"_self\" | \"_blank\" | \"_parent\" | \"_top\" | \"_unfencedTop\"; }",
                    "references": {
                        "ItemLink": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::ItemLink"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the hyperlink properties of the item. If this property is\ndefined, the `ch-navigation-list-item` will render an anchor tag with this\nproperties. Otherwise, it will render a button tag."
                }
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "NavigationListItemModel",
                    "resolved": "{ id?: string; caption: string; disabled?: boolean; expanded?: boolean; metadata?: string; startImgSrc?: string; startImgType?: \"mask\" | \"background\"; link?: ItemLink; items?: NavigationListModel; }",
                    "references": {
                        "NavigationListItemModel": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/components/navigation-list/types.ts::NavigationListItemModel"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the UI model of the control"
                }
            },
            "navigationListExpanded": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the navigation-list parent is expanded or collapsed."
                },
                "attribute": "navigation-list-expanded",
                "reflect": false,
                "defaultValue": "true"
            },
            "selected": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the hyperlink is selected. Only applies when the `link`\nproperty is defined."
                },
                "attribute": "selected",
                "reflect": false,
                "defaultValue": "false"
            },
            "selectedLinkIndicator": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the selected item indicator is displayed when the item is\nselected. Only applies when the `link` property is defined."
                },
                "attribute": "selected-link-indicator",
                "reflect": false,
                "defaultValue": "false"
            },
            "showCaptionOnCollapse": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"inline\" | \"tooltip\"",
                    "resolved": "\"inline\" | \"tooltip\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the caption will be displayed when the navigation-list\nparent is collapsed"
                },
                "attribute": "show-caption-on-collapse",
                "reflect": false,
                "defaultValue": "\"inline\""
            },
            "startImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the src of the start image."
                },
                "attribute": "start-img-src",
                "reflect": false
            },
            "startImgType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Exclude<ImageRender, \"img\">",
                    "resolved": "\"background\" | \"mask\"",
                    "references": {
                        "Exclude": {
                            "location": "global",
                            "id": "global::Exclude"
                        },
                        "ImageRender": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::ImageRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the start image will be rendered."
                },
                "attribute": "start-img-type",
                "reflect": false,
                "defaultValue": "\"background\""
            },
            "tooltipDelay": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the delay (in ms) for the tooltip to be displayed."
                },
                "attribute": "tooltip-delay",
                "reflect": false,
                "defaultValue": "100"
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "getImagePathCallback",
                "methodName": "getImagePathCallbackChanged"
            }];
    }
}
